import React, { useState } from 'react';

const AddEmployee = ({ onAdd = null }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [formData, setFormData] = useState({
    // Personal Information
    employeeId: '',
    firstName: '',
    lastName: '',
    middleName: '',
    dateOfBirth: '',
    gender: '',
    maritalStatus: '',
    nationality: '',
    bloodGroup: '',
    aadharNumber: '',
    panNumber: '',
    passportNumber: '',
    visaDetails: '',
    
    // Contact Information
    personalEmail: '',
    companyEmail: '',
    personalPhone: '',
    workPhone: '',
    emergencyContactName: '',
    emergencyContactRelation: '',
    emergencyContactPhone: '',
    permanentAddress: '',
    currentAddress: '',
    sameAsPermanent: false,
    city: '',
    state: '',
    pincode: '',
    country: 'India',
    
    // Professional Information
    employeeType: 'Full-Time',
    department: '',
    designation: '',
    role: 'employee',
    reportingManager: '',
    band: '',
    joiningDate: '',
    confirmationDate: '',
    probationEndDate: '',
    workLocation: '',
    workMode: 'Hybrid',
    shiftTiming: 'Day',
    isFresher: false,
    
    // Compensation & Benefits
    salary: '',
    ctc: '',
    basicSalary: '',
    hra: '',
    otherAllowances: '',
    variablePay: '',
    bankAccountNumber: '',
    bankName: '',
    ifscCode: '',
    pfNumber: '',
    uanNumber: '',
    esiNumber: '',
    
    // Skills & Education
    highestQualification: '',
    specialization: '',
    yearOfPassing: '',
    university: '',
    skills: '',
    certifications: '',
    totalExperience: '',
    relevantExperience: '',
    previousCompany: '',
    
    // Documents & Compliance
    offerLetterDate: '',
    bgvStatus: 'Pending',
    medicalFitness: 'Pending',
    contractSigned: false,
    ndaSigned: false,
    policyAccepted: false,
    
    // Additional Info
    hobbies: '',
    languagesKnown: '',
    linkedInProfile: '',
    githubProfile: '',
    notes: ''
  });

  // Document state
  const [documents, setDocuments] = useState({
    tenthMarksheet: null,
    tenthMarksheetName: '',
    twelfthMarksheet: null,
    twelfthMarksheetName: '',
    graduationCertificate: null,
    graduationCertificateName: '',
    graduationMarksheet: null,
    graduationMarksheetName: '',
    experienceLetter: null,
    experienceLetterName: '',
    relievingLetter: null,
    relievingLetterName: '',
    salarySlip1: null,
    salarySlip1Name: '',
    salarySlip2: null,
    salarySlip2Name: '',
    salarySlip3: null,
    salarySlip3Name: ''
  });

  const [errors, setErrors] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});

  // Date limits
  const minDate = "2025-01-01";
  const maxDate = "2099-12-31";

  // Constants for dropdowns
  const departments = [
    'Engineering', 'IT', 'Product Management', 'Marketing', 'Human Resources', 
    'R&D', 'Sales', 'Finance', 'Operations', 'Legal', 'Customer Support', 
    'Data Science', 'Security', 'Quality Assurance', 'Business Development',
    'Procurement', 'Supply Chain', 'Corporate Communications', 'Admin'
  ];

  const designations = {
    'Engineering': ['Software Engineer', 'Senior Software Engineer', 'Lead Software Engineer', 'Principal Engineer', 'Engineering Manager', 'Director of Engineering', 'CTO', 'DevOps Engineer', 'SRE', 'Frontend Developer', 'Backend Developer', 'Full Stack Developer'],
    'IT': ['IT Support', 'System Administrator', 'Network Engineer', 'IT Manager', 'Cloud Architect', 'Database Administrator', 'Security Analyst', 'IT Director', 'Infrastructure Lead'],
    'Product Management': ['Associate Product Manager', 'Product Manager', 'Senior Product Manager', 'Lead Product Manager', 'Director of Product', 'Product Owner', 'Technical Product Manager'],
    'Marketing': ['Marketing Executive', 'Digital Marketing Specialist', 'SEO Expert', 'Marketing Manager', 'Brand Manager', 'Content Strategist', 'Social Media Manager', 'Growth Hacker', 'CMO'],
    'Human Resources': ['HR Executive', 'HR Manager', 'Recruiter', 'Training Coordinator', 'HRBP', 'Talent Acquisition Lead', 'Compensation Analyst', 'HR Director', 'CHRO'],
    'R&D': ['Research Scientist', 'Product Developer', 'Innovation Lead', 'R&D Manager', 'Research Engineer', 'Prototype Specialist'],
    'Sales': ['Sales Executive', 'Business Development Manager', 'Account Manager', 'Sales Director', 'Regional Sales Manager', 'VP of Sales', 'Inside Sales Rep'],
    'Finance': ['Accountant', 'Finance Manager', 'Financial Analyst', 'Finance Controller', 'CFO', 'Tax Analyst', 'Auditor', 'Treasury Manager'],
    'Operations': ['Operations Manager', 'Project Coordinator', 'Process Analyst', 'Operations Director', 'COO', 'Facility Manager', 'Vendor Manager'],
    'Data Science': ['Data Analyst', 'Data Scientist', 'ML Engineer', 'AI Researcher', 'Analytics Manager', 'Data Engineer', 'BI Developer'],
    'Customer Support': ['Support Executive', 'Customer Success Manager', 'Support Lead', 'Technical Support Engineer', 'Customer Support Director']
  };

  const employeeTypes = ['Full-Time', 'Part-Time', 'Contract', 'Intern', 'Consultant', 'Temporary'];
  const workModes = ['Remote', 'Hybrid', 'On-site'];
  const shifts = ['Day', 'Evening', 'Night', 'Rotational', 'Flexible'];
  const bands = ['B1', 'B2', 'B3', 'B4', 'M1', 'M2', 'M3', 'D1', 'D2', 'C1', 'C2'];
  const genders = ['Male', 'Female', 'Prefer not to say'];
  const maritalStatuses = ['Single', 'Married'];
  const qualifications = ['B.Tech', 'M.Tech', 'BCA', 'MCA', 'MBA', 'B.Sc', 'M.Sc', 'PhD', 'BE', 'ME', 'B.Com', 'M.Com', 'Diploma', 'Other'];
  
  const bloodGroups = ['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'];
  const roles = ['employee', 'manager', 'hr', 'admin', 'supervisor', 'director', 'vp', 'cxo'];
  const bgvStatuses = ['Pending', 'In Progress', 'Cleared', 'Failed', 'On Hold'];
  const medicalStatuses = ['Pending', 'Fit', 'Unfit', 'Review Required'];

  // Page definitions
  const pages = [
    {
      title: "Personal Information",
      sections: ["Employee Identification", "Contact & Demographic Information"]
    },
    {
      title: "Address & Emergency Contact",
      sections: ["Address Information", "Emergency Contact"]
    },
    {
      title: "Professional Information",
      sections: ["Professional Information"]
    },
    {
      title: "Compensation & Banking",
      sections: ["Compensation & Banking Details"]
    },
    {
      title: "Education & Skills",
      sections: ["Education & Skills"]
    },
    {
      title: "Document Upload",
      sections: ["Educational Documents", "Experience Documents (If Applicable)"]
    },
    {
      title: "Compliance & Additional Info",
      sections: ["Documents & Compliance", "Additional Information"]
    }
  ];

  // Helper function to validate email format
  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Helper function to limit text input to 100 characters
  const limitText = (value) => {
    if (!value) return value;
    return value.slice(0, 100);
  };

  // Helper function to limit numbers to 10 digits
  const limitNumber = (value) => {
    if (!value) return value;
    // Remove any non-digit characters
    const digitsOnly = value.toString().replace(/\D/g, '');
    return digitsOnly.slice(0, 10);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let newValue = type === 'checkbox' ? checked : value;
    
    // Apply limits based on field type
    if (type !== 'checkbox') {
      // Text fields with 100 character limit
      const textFields = ['firstName', 'lastName', 'middleName', 'nationality', 'visaDetails', 'bankName', 
                          'specialization', 'university', 'skills', 'certifications', 'previousCompany', 
                          'hobbies', 'languagesKnown', 'notes', 'emergencyContactName', 'emergencyContactRelation',
                          'city', 'state', 'country', 'workLocation', 'reportingManager', 'permanentAddress', 
                          'currentAddress', 'linkedInProfile', 'githubProfile'];
      
      // Number fields with 10 digit limit
      const numberFields = ['personalPhone', 'workPhone', 'emergencyContactPhone', 'aadharNumber', 
                            'bankAccountNumber', 'pfNumber', 'uanNumber', 'esiNumber'];
      
      // Special numeric fields without digit limit
      const salaryFields = ['salary', 'ctc', 'basicSalary', 'hra', 'otherAllowances', 'variablePay', 
                            'totalExperience', 'relevantExperience'];
      
      if (textFields.includes(name)) {
        newValue = limitText(value);
      } else if (numberFields.includes(name)) {
        newValue = limitNumber(value);
      } else if (salaryFields.includes(name)) {
        // Allow decimal points for salary fields
        const validChars = value.replace(/[^0-9.]/g, '');
        // Ensure only one decimal point
        const parts = validChars.split('.');
        let formattedValue = parts[0];
        if (parts.length > 1) {
          formattedValue += '.' + parts[1].slice(0, 2);
        }
        newValue = formattedValue;
      }
      
      // Special handling for PAN (10 chars alphanumeric)
      if (name === 'panNumber') {
        newValue = value.slice(0, 10).toUpperCase();
      }
      
      // Pincode (6 digits)
      if (name === 'pincode') {
        newValue = value.replace(/\D/g, '').slice(0, 6);
      }
      
      // IFSC code (11 chars alphanumeric)
      if (name === 'ifscCode') {
        newValue = value.slice(0, 11).toUpperCase();
      }
      
      // Employee ID (20 chars limit)
      if (name === 'employeeId') {
        newValue = value.slice(0, 20);
      }
    }
    
    setFormData(prev => ({ ...prev, [name]: newValue }));
    
    // Handle "same as permanent address" logic
    if (name === 'sameAsPermanent' && checked) {
      setFormData(prev => ({ 
        ...prev, 
        sameAsPermanent: true,
        currentAddress: prev.permanentAddress,
        city: prev.city,
        state: prev.state,
        pincode: prev.pincode
      }));
    } else if (name === 'sameAsPermanent' && !checked) {
      setFormData(prev => ({ ...prev, sameAsPermanent: false }));
    }
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileUpload = (e, documentType) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!validTypes.includes(file.type)) {
        alert('Please upload PDF, JPEG, or PNG files only');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size should be less than 5MB');
        return;
      }
      
      // Simulate upload progress
      setUploadProgress(prev => ({ ...prev, [documentType]: 0 }));
      const interval = setInterval(() => {
        setUploadProgress(prev => {
          const newProgress = (prev[documentType] || 0) + 10;
          if (newProgress >= 100) {
            clearInterval(interval);
            return { ...prev, [documentType]: 100 };
          }
          return { ...prev, [documentType]: newProgress };
        });
      }, 100);
      
      setTimeout(() => {
        setDocuments(prev => ({
          ...prev,
          [documentType]: file,
          [`${documentType}Name`]: file.name
        }));
        clearInterval(interval);
        setUploadProgress(prev => ({ ...prev, [documentType]: 100 }));
      }, 1000);
    }
  };

  const removeDocument = (documentType) => {
    setDocuments(prev => ({
      ...prev,
      [documentType]: null,
      [`${documentType}Name`]: ''
    }));
    setUploadProgress(prev => ({ ...prev, [documentType]: 0 }));
  };

  const validatePage = () => {
    const newErrors = {};
    
    if (currentPage === 0) {
      if (!formData.firstName?.trim()) newErrors.firstName = 'First name is required';
      if (!formData.lastName?.trim()) newErrors.lastName = 'Last name is required';
      if (!formData.companyEmail?.trim()) {
        newErrors.companyEmail = 'Company email is required';
      } else if (!isValidEmail(formData.companyEmail)) {
        newErrors.companyEmail = 'Enter a valid email address (e.g., name@company.com)';
      }
      if (formData.personalEmail && !isValidEmail(formData.personalEmail)) {
        newErrors.personalEmail = 'Enter a valid email address';
      }
      if (!formData.personalPhone?.trim()) {
        newErrors.personalPhone = 'Phone number is required';
      } else if (formData.personalPhone.length !== 10) {
        newErrors.personalPhone = 'Phone number must be exactly 10 digits';
      }
      if (formData.workPhone && formData.workPhone.length !== 10) {
        newErrors.workPhone = 'Work phone must be exactly 10 digits';
      }
    } else if (currentPage === 2) {
      if (!formData.department) newErrors.department = 'Department is required';
      if (!formData.designation) newErrors.designation = 'Designation is required';
      if (!formData.joiningDate) newErrors.joiningDate = 'Joining date is required';
      if (!formData.employeeId) newErrors.employeeId = 'Employee ID is required';
      if (!formData.workLocation) newErrors.workLocation = 'Work location is required';
    } else if (currentPage === 3) {
      if (!formData.salary) newErrors.salary = 'Salary is required';
    } else if (currentPage === 5) {
      // Validate document uploads
      if (!documents.tenthMarksheet) newErrors.tenthMarksheet = '10th Marksheet is required';
      if (!documents.twelfthMarksheet) newErrors.twelfthMarksheet = '12th Marksheet is required';
      if (!documents.graduationCertificate) newErrors.graduationCertificate = 'Graduation Certificate is required';
      
      if (!formData.isFresher) {
        if (!documents.experienceLetter) newErrors.experienceLetter = 'Experience Letter is required for experienced candidates';
        if (!documents.relievingLetter) newErrors.relievingLetter = 'Relieving Letter is required for experienced candidates';
        if (!documents.salarySlip1) newErrors.salarySlip1 = 'Latest Salary Slip is required for experienced candidates';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextPage = () => {
    if (validatePage()) {
      setCurrentPage(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const prevPage = () => {
    setCurrentPage(prev => prev - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validatePage()) {
      setShowConfirmation(true);
    }
  };

  const confirmSubmit = () => {
    const employeeCode = formData.employeeId || `EMP${Date.now()}`;
    const fullName = `${formData.firstName} ${formData.middleName ? formData.middleName + ' ' : ''}${formData.lastName}`;
    
    const employeeData = {
      ...formData,
      ...documents,
      employeeCode,
      fullName,
      createdAt: new Date().toISOString(),
      status: 'Active',
      probationStatus: new Date(formData.joiningDate) > new Date() ? 'Not Started' : 'In Progress',
      documentsUploaded: {
        tenth: !!documents.tenthMarksheet,
        twelfth: !!documents.twelfthMarksheet,
        graduation: !!documents.graduationCertificate,
        experience: !formData.isFresher && !!documents.experienceLetter,
        relieving: !formData.isFresher && !!documents.relievingLetter,
        salarySlips: !formData.isFresher && !!documents.salarySlip1
      }
    };

    if (typeof onAdd === 'function') {
      onAdd(employeeData);
    } else {
      const storedEmployees = JSON.parse(localStorage.getItem('employees') || '[]');
      const newEmployee = {
        ...employeeData,
        id: storedEmployees.length + 1,
        employeeId: employeeData.employeeId || `EMP${String(storedEmployees.length + 1).padStart(3, '0')}`
      };
      storedEmployees.push(newEmployee);
      localStorage.setItem('employees', JSON.stringify(storedEmployees));
    }

    resetForm();
    setShowConfirmation(false);
    alert('Employee added successfully!');
  };

  const resetForm = () => {
    setCurrentPage(0);
    setFormData({
      employeeId: '',
      firstName: '',
      lastName: '',
      middleName: '',
      dateOfBirth: '',
      gender: '',
      maritalStatus: '',
      nationality: '',
      bloodGroup: '',
      aadharNumber: '',
      panNumber: '',
      passportNumber: '',
      visaDetails: '',
      personalEmail: '',
      companyEmail: '',
      personalPhone: '',
      workPhone: '',
      emergencyContactName: '',
      emergencyContactRelation: '',
      emergencyContactPhone: '',
      permanentAddress: '',
      currentAddress: '',
      sameAsPermanent: false,
      city: '',
      state: '',
      pincode: '',
      country: 'India',
      employeeType: 'Full-Time',
      department: '',
      designation: '',
      role: 'employee',
      reportingManager: '',
      band: '',
      joiningDate: '',
      confirmationDate: '',
      probationEndDate: '',
      workLocation: '',
      workMode: 'Hybrid',
      shiftTiming: 'Day',
      isFresher: false,
      salary: '',
      ctc: '',
      basicSalary: '',
      hra: '',
      otherAllowances: '',
      variablePay: '',
      bankAccountNumber: '',
      bankName: '',
      ifscCode: '',
      pfNumber: '',
      uanNumber: '',
      esiNumber: '',
      highestQualification: '',
      specialization: '',
      yearOfPassing: '',
      university: '',
      skills: '',
      certifications: '',
      totalExperience: '',
      relevantExperience: '',
      previousCompany: '',
      offerLetterDate: '',
      bgvStatus: 'Pending',
      medicalFitness: 'Pending',
      contractSigned: false,
      ndaSigned: false,
      policyAccepted: false,
      hobbies: '',
      languagesKnown: '',
      linkedInProfile: '',
      githubProfile: '',
      notes: ''
    });
    setDocuments({
      tenthMarksheet: null,
      tenthMarksheetName: '',
      twelfthMarksheet: null,
      twelfthMarksheetName: '',
      graduationCertificate: null,
      graduationCertificateName: '',
      graduationMarksheet: null,
      graduationMarksheetName: '',
      experienceLetter: null,
      experienceLetterName: '',
      relievingLetter: null,
      relievingLetterName: '',
      salarySlip1: null,
      salarySlip1Name: '',
      salarySlip2: null,
      salarySlip2Name: '',
      salarySlip3: null,
      salarySlip3Name: ''
    });
  };

  const availableDesignations = formData.department ? designations[formData.department] || [] : [];

  // Render different pages
  const renderPage = () => {
    switch(currentPage) {
      case 0:
        return (
          <div className="space-y-8">
            {/* Employee Identification */}
            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b-2 border-blue-200 pb-2">Employee Identification</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employee ID *</label>
                  <input type="text" name="employeeId" value={formData.employeeId} onChange={handleChange} maxLength="20"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.employeeId ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="EMP001" />
                  {errors.employeeId && <p className="text-red-500 text-xs mt-1">{errors.employeeId}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                  <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} maxLength="100"
                    className={`w-full px-3 py-2 border rounded-lg ${errors.firstName ? 'border-red-500' : 'border-gray-300'}`} />
                  {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                  <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} maxLength="100"
                    className={`w-full px-3 py-2 border rounded-lg ${errors.lastName ? 'border-red-500' : 'border-gray-300'}`} />
                  {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Middle Name</label>
                  <input type="text" name="middleName" value={formData.middleName} onChange={handleChange} maxLength="100"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                  <select name="gender" value={formData.gender} onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                    <option value="">Select</option>
                    {genders.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                </div>
              </div>
            </section>

            {/* Contact & Demographic Info */}
            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b-2 border-blue-200 pb-2">Contact & Demographic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Email *</label>
                  <input type="email" name="companyEmail" value={formData.companyEmail} onChange={handleChange} maxLength="100"
                    className={`w-full px-3 py-2 border rounded-lg ${errors.companyEmail ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="name@company.com" />
                  {errors.companyEmail && <p className="text-red-500 text-xs mt-1">{errors.companyEmail}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Personal Email</label>
                  <input type="email" name="personalEmail" value={formData.personalEmail} onChange={handleChange} maxLength="100"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="name@example.com" />
                  {errors.personalEmail && <p className="text-red-500 text-xs mt-1">{errors.personalEmail}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Personal Phone *</label>
                  <input type="tel" name="personalPhone" value={formData.personalPhone} onChange={handleChange} maxLength="10"
                    className={`w-full px-3 py-2 border rounded-lg ${errors.personalPhone ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="1234567890" />
                  {errors.personalPhone && <p className="text-red-500 text-xs mt-1">{errors.personalPhone}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Work Phone</label>
                  <input type="tel" name="workPhone" value={formData.workPhone} onChange={handleChange} maxLength="10"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="1234567890" />
                  {errors.workPhone && <p className="text-red-500 text-xs mt-1">{errors.workPhone}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                  <input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleChange}
                    min={minDate} max={maxDate}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Blood Group</label>
                  <select name="bloodGroup" value={formData.bloodGroup} onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                    <option value="">Select</option>
                    {bloodGroups.map(bg => <option key={bg} value={bg}>{bg}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Marital Status</label>
                  <select name="maritalStatus" value={formData.maritalStatus} onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                    <option value="">Select</option>
                    {maritalStatuses.map(ms => <option key={ms} value={ms}>{ms}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nationality</label>
                  <input type="text" name="nationality" value={formData.nationality} onChange={handleChange} maxLength="100"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Indian" />
                </div>
              </div>
            </section>
          </div>
        );
      
      case 1:
        return (
          <div className="space-y-8">
            {/* Address Information */}
            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b-2 border-blue-200 pb-2">Address Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Permanent Address</label>
                  <textarea name="permanentAddress" rows="2" value={formData.permanentAddress} onChange={handleChange} maxLength="500"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input type="text" name="city" value={formData.city} onChange={handleChange} maxLength="100"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <input type="text" name="state" value={formData.state} onChange={handleChange} maxLength="100"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
                  <input type="text" name="pincode" value={formData.pincode} onChange={handleChange} maxLength="6"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                  <input type="text" name="country" value={formData.country} onChange={handleChange} maxLength="100"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div className="md:col-span-2">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="sameAsPermanent" checked={formData.sameAsPermanent} onChange={handleChange}
                      className="rounded border-gray-300" />
                    <span className="text-sm text-gray-700">Current address same as permanent address</span>
                  </label>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Address</label>
                  <textarea name="currentAddress" rows="2" value={formData.currentAddress} onChange={handleChange} maxLength="500"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
              </div>
            </section>

            {/* Emergency Contact */}
            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b-2 border-blue-200 pb-2">Emergency Contact</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
                  <input type="text" name="emergencyContactName" value={formData.emergencyContactName} onChange={handleChange} maxLength="100"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Relationship</label>
                  <input type="text" name="emergencyContactRelation" value={formData.emergencyContactRelation} onChange={handleChange} maxLength="100"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                  <input type="tel" name="emergencyContactPhone" value={formData.emergencyContactPhone} onChange={handleChange} maxLength="10"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
                  <input type="text" name="emergencyContactName" value={formData.emergencyContactNames} onChange={handleChange} maxLength="100"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Relationship</label>
                  <input type="text" name="emergencyContactRelation" value={formData.emergencyContactRelations} onChange={handleChange} maxLength="100"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                  <input type="tel" name="emergencyContactPhone" value={formData.emergencyContactPhones} onChange={handleChange} maxLength="10"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
              </div>
            </section>
          </div>
        );
      
      case 2:
        return (
          <section>
            <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b-2 border-blue-200 pb-2">Professional Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Employee Type</label>
                <select name="employeeType" value={formData.employeeType} onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                  {employeeTypes.map(et => <option key={et} value={et}>{et}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <input type="checkbox" name="isFresher" checked={formData.isFresher} onChange={handleChange}
                    className="mr-2 rounded border-gray-300" />
                  Fresher (No previous work experience)
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Department *</label>
                <select name="department" value={formData.department} onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg ${errors.department ? 'border-red-500' : 'border-gray-300'}`}>
                  <option value="">Select Department</option>
                  {departments.map(dept => <option key={dept} value={dept}>{dept}</option>)}
                </select>
                {errors.department && <p className="text-red-500 text-xs mt-1">{errors.department}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Designation *</label>
                <select name="designation" value={formData.designation} onChange={handleChange}
                  disabled={!formData.department}
                  className={`w-full px-3 py-2 border rounded-lg ${errors.designation ? 'border-red-500' : 'border-gray-300'} ${!formData.department ? 'bg-gray-100' : ''}`}>
                  <option value="">Select Designation</option>
                  {availableDesignations.map(des => <option key={des} value={des}>{des}</option>)}
                </select>
                {errors.designation && <p className="text-red-500 text-xs mt-1">{errors.designation}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select name="role" value={formData.role} onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                  {roles.map(role => <option key={role} value={role}>{role.toUpperCase()}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Band/Grade</label>
                <select name="band" value={formData.band} onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                  <option value="">Select Band</option>
                  {bands.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reporting Manager</label>
                <input type="text" name="reportingManager" value={formData.reportingManager} onChange={handleChange} maxLength="100"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Manager Name/ID" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Work Location *</label>
                <input type="text" name="workLocation" value={formData.workLocation} onChange={handleChange} maxLength="100"
                  className={`w-full px-3 py-2 border rounded-lg ${errors.workLocation ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="City/Office Name" />
                {errors.workLocation && <p className="text-red-500 text-xs mt-1">{errors.workLocation}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Work Mode</label>
                <select name="workMode" value={formData.workMode} onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                  {workModes.map(wm => <option key={wm} value={wm}>{wm}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Shift Timing</label>
                <select name="shiftTiming" value={formData.shiftTiming} onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                  {shifts.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Joining Date *</label>
                <input type="date" name="joiningDate" value={formData.joiningDate} onChange={handleChange}
                  min={minDate} max={maxDate}
                  className={`w-full px-3 py-2 border rounded-lg ${errors.joiningDate ? 'border-red-500' : 'border-gray-300'}`} />
                {errors.joiningDate && <p className="text-red-500 text-xs mt-1">{errors.joiningDate}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Probation End Date</label>
                <input type="date" name="probationEndDate" value={formData.probationEndDate} onChange={handleChange}
                  min={minDate} max={maxDate}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirmation Date</label>
                <input type="date" name="confirmationDate" value={formData.confirmationDate} onChange={handleChange}
                  min={minDate} max={maxDate}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              {!formData.isFresher && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Total Experience (Years)</label>
                    <input type="text" name="totalExperience" value={formData.totalExperience} onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="e.g., 5.5" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Relevant Experience (Years)</label>
                    <input type="text" name="relevantExperience" value={formData.relevantExperience} onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="e.g., 3.5" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Previous Company</label>
                    <input type="text" name="previousCompany" value={formData.previousCompany} onChange={handleChange} maxLength="100"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                  </div>
                </>
              )}
            </div>
          </section>
        );
      
      case 3:
        return (
          <section>
            <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b-2 border-blue-200 pb-2">Compensation & Banking Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Annual CTC (₹)</label>
                <input type="text" name="ctc" value={formData.ctc} onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Total CTC" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Basic Salary (₹) *</label>
                <input type="text" name="salary" value={formData.salary} onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg ${errors.salary ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Monthly Basic" />
                {errors.salary && <p className="text-red-500 text-xs mt-1">{errors.salary}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">HRA (₹)</label>
                <input type="text" name="hra" value={formData.hra} onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Other Allowances (₹)</label>
                <input type="text" name="otherAllowances" value={formData.otherAllowances} onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Variable Pay (₹)</label>
                <input type="text" name="variablePay" value={formData.variablePay} onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div className="md:col-span-2">
                <h4 className="font-medium text-gray-700 mb-2">Bank Account Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Account Number</label>
                    <input type="text" name="bankAccountNumber" value={formData.bankAccountNumber} onChange={handleChange} maxLength="25"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Bank Name</label>
                    <input type="text" name="bankName" value={formData.bankName} onChange={handleChange} maxLength="100"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">IFSC Code</label>
                    <input type="text" name="ifscCode" value={formData.ifscCode} onChange={handleChange} maxLength="11"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">PF Number</label>
                <input type="text" name="pfNumber" value={formData.pfNumber} onChange={handleChange} maxLength="22"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">UAN Number</label>
                <input type="text" name="uanNumber" value={formData.uanNumber} onChange={handleChange} maxLength="22"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ESI Number</label>
                <input type="text" name="esiNumber" value={formData.esiNumber} onChange={handleChange} maxLength="20"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
            </div>
          </section>
        );
      
      case 4:
        return (
          <section>
            <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b-2 border-blue-200 pb-2">Education & Skills</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Highest Qualification</label>
                <select name="highestQualification" value={formData.highestQualification} onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                  <option value="">Select</option>
                  {qualifications.map(q => <option key={q} value={q}>{q}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Specialization</label>
                <input type="text" name="specialization" value={formData.specialization} onChange={handleChange} maxLength="500"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year of Passing</label>
                <input type="number" name="yearOfPassing" value={formData.yearOfPassing} onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg" min="1950" max="2099" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">University/Institute</label>
                <input type="text" name="university" value={formData.university} onChange={handleChange} maxLength="500"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Skills (comma separated)</label>
                <textarea name="skills" rows="2" value={formData.skills} onChange={handleChange} maxLength="500"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="React, Node.js, Python, AWS, MongoDB" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Certifications</label>
                <textarea name="certifications" rows="2" value={formData.certifications} onChange={handleChange} maxLength="500"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="AWS Certified, PMP, Scrum Master" />
              </div>
            </div>
          </section>
        );
      
      case 5:
        return (
          <div className="space-y-8">
            {/* Educational Documents */}
            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b-2 border-blue-200 pb-2">Educational Documents</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="border rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">10th Marksheet *</label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                    <input type="file" id="tenthMarksheet" className="hidden" onChange={(e) => handleFileUpload(e, 'tenthMarksheet')}
                      accept=".pdf,.jpg,.jpeg,.png" />
                    <label htmlFor="tenthMarksheet" className="cursor-pointer text-blue-600 hover:text-blue-700">
                      <i className="fas fa-cloud-upload-alt text-2xl"></i>
                      <p className="mt-1">Click to upload</p>
                    </label>
                    {documents.tenthMarksheetName && (
                      <div className="mt-2 text-sm text-green-600">
                        <p>✓ {documents.tenthMarksheetName}</p>
                        {uploadProgress.tenthMarksheet === 100 && <button onClick={() => removeDocument('tenthMarksheet')}
                          className="text-red-500 hover:text-red-700 ml-2">Remove</button>}
                      </div>
                    )}
                    {uploadProgress.tenthMarksheet > 0 && uploadProgress.tenthMarksheet < 100 && (
                      <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                        <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${uploadProgress.tenthMarksheet}%` }}></div>
                      </div>
                    )}
                  </div>
                  {errors.tenthMarksheet && <p className="text-red-500 text-xs mt-1">{errors.tenthMarksheet}</p>}
                </div>

                <div className="border rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">12th Marksheet *</label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                    <input type="file" id="twelfthMarksheet" className="hidden" onChange={(e) => handleFileUpload(e, 'twelfthMarksheet')}
                      accept=".pdf,.jpg,.jpeg,.png" />
                    <label htmlFor="twelfthMarksheet" className="cursor-pointer text-blue-600 hover:text-blue-700">
                      <i className="fas fa-cloud-upload-alt text-2xl"></i>
                      <p className="mt-1">Click to upload</p>
                    </label>
                    {documents.twelfthMarksheetName && (
                      <div className="mt-2 text-sm text-green-600">
                        <p>✓ {documents.twelfthMarksheetName}</p>
                        <button onClick={() => removeDocument('twelfthMarksheet')} className="text-red-500 hover:text-red-700 ml-2">Remove</button>
                      </div>
                    )}
                  </div>
                  {errors.twelfthMarksheet && <p className="text-red-500 text-xs mt-1">{errors.twelfthMarksheet}</p>}
                </div>

                <div className="border rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Graduation Certificate *</label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                    <input type="file" id="graduationCertificate" className="hidden" onChange={(e) => handleFileUpload(e, 'graduationCertificate')}
                      accept=".pdf,.jpg,.jpeg,.png" />
                    <label htmlFor="graduationCertificate" className="cursor-pointer text-blue-600 hover:text-blue-700">
                      <i className="fas fa-cloud-upload-alt text-2xl"></i>
                      <p className="mt-1">Click to upload</p>
                    </label>
                    {documents.graduationCertificateName && (
                      <div className="mt-2 text-sm text-green-600">
                        <p>✓ {documents.graduationCertificateName}</p>
                        <button onClick={() => removeDocument('graduationCertificate')} className="text-red-500 hover:text-red-700 ml-2">Remove</button>
                      </div>
                    )}
                  </div>
                  {errors.graduationCertificate && <p className="text-red-500 text-xs mt-1">{errors.graduationCertificate}</p>}
                </div>

                <div className="border rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Graduation Marksheets (All Semesters)</label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                    <input type="file" id="graduationMarksheet" className="hidden" onChange={(e) => handleFileUpload(e, 'graduationMarksheet')}
                      accept=".pdf,.jpg,.jpeg,.png" multiple />
                    <label htmlFor="graduationMarksheet" className="cursor-pointer text-blue-600 hover:text-blue-700">
                      <i className="fas fa-cloud-upload-alt text-2xl"></i>
                      <p className="mt-1">Upload (PDF/Zip)</p>
                    </label>
                    {documents.graduationMarksheetName && (
                      <div className="mt-2 text-sm text-green-600">
                        <p>✓ {documents.graduationMarksheetName}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>

            {/* Experience Documents (Conditional) */}
            {!formData.isFresher && (
              <section>
                <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b-2 border-blue-200 pb-2">Experience Documents</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="border rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Experience Letter *</label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                      <input type="file" id="experienceLetter" className="hidden" onChange={(e) => handleFileUpload(e, 'experienceLetter')}
                        accept=".pdf,.jpg,.jpeg,.png" />
                      <label htmlFor="experienceLetter" className="cursor-pointer text-blue-600 hover:text-blue-700">
                        <i className="fas fa-cloud-upload-alt text-2xl"></i>
                        <p className="mt-1">Click to upload</p>
                      </label>
                      {documents.experienceLetterName && (
                        <div className="mt-2 text-sm text-green-600">
                          <p>✓ {documents.experienceLetterName}</p>
                          <button onClick={() => removeDocument('experienceLetter')} className="text-red-500 hover:text-red-700 ml-2">Remove</button>
                        </div>
                      )}
                    </div>
                    {errors.experienceLetter && <p className="text-red-500 text-xs mt-1">{errors.experienceLetter}</p>}
                  </div>

                  <div className="border rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Relieving Letter *</label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                      <input type="file" id="relievingLetter" className="hidden" onChange={(e) => handleFileUpload(e, 'relievingLetter')}
                        accept=".pdf,.jpg,.jpeg,.png" />
                      <label htmlFor="relievingLetter" className="cursor-pointer text-blue-600 hover:text-blue-700">
                        <i className="fas fa-cloud-upload-alt text-2xl"></i>
                        <p className="mt-1">Click to upload</p>
                      </label>
                      {documents.relievingLetterName && (
                        <div className="mt-2 text-sm text-green-600">
                          <p>✓ {documents.relievingLetterName}</p>
                          <button onClick={() => removeDocument('relievingLetter')} className="text-red-500 hover:text-red-700 ml-2">Remove</button>
                        </div>
                      )}
                    </div>
                    {errors.relievingLetter && <p className="text-red-500 text-xs mt-1">{errors.relievingLetter}</p>}
                  </div>

                  <div className="border rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Latest Salary Slip *</label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                      <input type="file" id="salarySlip1" className="hidden" onChange={(e) => handleFileUpload(e, 'salarySlip1')}
                        accept=".pdf,.jpg,.jpeg,.png" />
                      <label htmlFor="salarySlip1" className="cursor-pointer text-blue-600 hover:text-blue-700">
                        <i className="fas fa-cloud-upload-alt text-2xl"></i>
                        <p className="mt-1">Click to upload</p>
                      </label>
                      {documents.salarySlip1Name && (
                        <div className="mt-2 text-sm text-green-600">
                          <p>✓ {documents.salarySlip1Name}</p>
                          <button onClick={() => removeDocument('salarySlip1')} className="text-red-500 hover:text-red-700 ml-2">Remove</button>
                        </div>
                      )}
                    </div>
                    {errors.salarySlip1 && <p className="text-red-500 text-xs mt-1">{errors.salarySlip1}</p>}
                  </div>

                  <div className="border rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Previous Salary Slips (Optional)</label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                      <input type="file" id="salarySlip2" className="hidden" onChange={(e) => handleFileUpload(e, 'salarySlip2')}
                        accept=".pdf,.jpg,.jpeg,.png" />
                      <label htmlFor="salarySlip2" className="cursor-pointer text-blue-600 hover:text-blue-700">
                        <i className="fas fa-cloud-upload-alt text-2xl"></i>
                        <p className="mt-1">Upload additional</p>
                      </label>
                      {documents.salarySlip2Name && <div className="mt-2 text-sm text-green-600">✓ {documents.salarySlip2Name}</div>}
                    </div>
                  </div>
                </div>
              </section>
            )}
          </div>
        );
      
      case 6:
        return (
          <div className="space-y-8">
            {/* Documents & Compliance */}
            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b-2 border-blue-200 pb-2">Documents & Compliance</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Aadhar Number</label>
                  <input type="text" name="aadharNumber" value={formData.aadharNumber} onChange={handleChange} maxLength="12"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PAN Number</label>
                  <input type="text" name="panNumber" value={formData.panNumber} onChange={handleChange} maxLength="10"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="ABCDE1234F" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Passport Number</label>
                  <input type="text" name="passportNumber" value={formData.passportNumber} onChange={handleChange} maxLength="20"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Background Verification Status</label>
                  <select name="bgvStatus" value={formData.bgvStatus} onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                    {bgvStatuses.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Medical Fitness Status</label>
                  <select name="medicalFitness" value={formData.medicalFitness} onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                    {medicalStatuses.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Offer Letter Date</label>
                  <input type="date" name="offerLetterDate" value={formData.offerLetterDate} onChange={handleChange}
                    min={minDate} max={maxDate}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div className="md:col-span-2">
                  <div className="flex gap-4 flex-wrap">
                    <label className="flex items-center gap-2">
                      <input type="checkbox" name="contractSigned" checked={formData.contractSigned} onChange={handleChange}
                        className="rounded border-gray-300" />
                      <span className="text-sm text-gray-700">Employment Contract Signed</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" name="ndaSigned" checked={formData.ndaSigned} onChange={handleChange}
                        className="rounded border-gray-300" />
                      <span className="text-sm text-gray-700">NDA Signed</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" name="policyAccepted" checked={formData.policyAccepted} onChange={handleChange}
                        className="rounded border-gray-300" />
                      <span className="text-sm text-gray-700">HR Policy Accepted</span>
                    </label>
                  </div>
                </div>
              </div>
            </section>

            {/* Additional Information */}
            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b-2 border-blue-200 pb-2">Additional Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Languages Known</label>
                  <input type="text" name="languagesKnown" value={formData.languagesKnown} onChange={handleChange} maxLength="100"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="English, Hindi, Spanish" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn Profile</label>
                  <input type="url" name="linkedInProfile" value={formData.linkedInProfile} onChange={handleChange} maxLength="100"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">GitHub Profile</label>
                  <input type="url" name="githubProfile" value={formData.githubProfile} onChange={handleChange} maxLength="100"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hobbies/Interests</label>
                  <input type="text" name="hobbies" value={formData.hobbies} onChange={handleChange} maxLength="100"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Additional Notes</label>
                  <textarea name="notes" rows="3" value={formData.notes} onChange={handleChange} maxLength="100"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Any additional information about the employee" />
                </div>
              </div>
            </section>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4">
      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold mb-4">Confirm Employee Addition</h3>
            <p className="text-gray-600 mb-4">Please verify the following details:</p>
            <div className="bg-gray-50 p-4 rounded-lg mb-4 max-h-96 overflow-y-auto">
              <p><strong>Name:</strong> {formData.firstName} {formData.lastName}</p>
              <p><strong>Employee ID:</strong> {formData.employeeId}</p>
              <p><strong>Department:</strong> {formData.department}</p>
              <p><strong>Designation:</strong> {formData.designation}</p>
              <p><strong>Email:</strong> {formData.companyEmail}</p>
              <p><strong>Employee Type:</strong> {formData.isFresher ? 'Fresher' : 'Experienced'}</p>
              <p><strong>Documents Uploaded:</strong></p>
              <ul className="list-disc list-inside ml-4 text-sm">
                <li>10th Marksheet: {documents.tenthMarksheet ? '✓' : '✗'}</li>
                <li>12th Marksheet: {documents.twelfthMarksheet ? '✓' : '✗'}</li>
                <li>Graduation Certificate: {documents.graduationCertificate ? '✓' : '✗'}</li>
                {!formData.isFresher && (
                  <>
                    <li>Experience Letter: {documents.experienceLetter ? '✓' : '✗'}</li>
                    <li>Relieving Letter: {documents.relievingLetter ? '✓' : '✗'}</li>
                    <li>Salary Slip: {documents.salarySlip1 ? '✓' : '✗'}</li>
                  </>
                )}
              </ul>
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowConfirmation(false)} className="px-4 py-2 border rounded-lg">Cancel</button>
              <button onClick={confirmSubmit} className="px-4 py-2 bg-blue-600 text-white rounded-lg">Confirm</button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
          <h2 className="text-2xl font-semibold text-gray-800">Employee Onboarding Form</h2>
          <p className="text-sm text-gray-600 mt-1">Please complete all required fields (*) for employee record</p>
          
          {/* Progress Bar */}
          <div className="mt-4">
            <div className="flex justify-between mb-2">
              <span className="text-sm text-gray-600">Step {currentPage + 1} of {pages.length}</span>
              <span className="text-sm text-gray-600">{pages[currentPage].title}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                style={{ width: `${((currentPage + 1) / pages.length) * 100}%` }}></div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {renderPage()}

          {/* Pagination Buttons */}
          <div className="flex gap-3 justify-between pt-6 mt-8 border-t">
            <div>
              {currentPage > 0 && (
                <button type="button" onClick={prevPage}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition">
                  ← Previous
                </button>
              )}
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => window.history.back()}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition">
                Cancel
              </button>
              {currentPage < pages.length - 1 ? (
                <button type="button" onClick={nextPage}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow-md">
                  Next →
                </button>
              ) : (
                <button type="submit"
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition shadow-md">
                  Submit Employee
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEmployee; 